import {
  useRef,
  createContext,
  useEffect,
  useState,
  useContext,
  HTMLAttributes,
  forwardRef,
} from "react";

import {
  SavedItems,
  AddSavedItemsResponse,
  SavedItemsExpandOption,
} from "@repo/apis/customers-saveditems-v3";

import {
  SavedItemsSdkInstance,
  initializeSavedItemsSdk,
} from "@repo/saved-items";
import { isProductSaved, findSavedItemId } from "@repo/saved-items";
import * as AsyncAction from "@repo/ui/async-action";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";

import {
  DeferredSavedItemsSdk,
  SavedItemContextState,
  Status,
  SavedItemsProviderProps,
} from "./saved-items.types";

type SaveForLaterProps = HTMLAttributes<HTMLDivElement> & {
  productId: number;
  variantId?: number;
  colourWayId?: number;
  onAdded?: (params: { productId: number }) => void;
};

export const SaveForLater = forwardRef<HTMLDivElement, SaveForLaterProps>(
  (
    { className, productId, variantId, colourWayId, onAdded, ...props },
    ref,
  ) => {
    const {
      savedItems,
      addSavedItems,
      removeSavedItems,
      status: savedItemsStatus,
      SavedIcon,
      UnsavedIcon,
    } = useSavedItems();
    const [optimisticSaved, setOptimisticSaved] = useState<boolean | null>(
      null,
    );

    const isSaved = isProductSaved(savedItems, productId, variantId);
    const savedItem = findSavedItemId(savedItems, productId, variantId);

    const displaySaved = optimisticSaved !== null ? optimisticSaved : isSaved;

    const handleOnClick = async (
      e: React.MouseEvent<HTMLButtonElement>,
    ): Promise<boolean> => {
      e.preventDefault();
      e.stopPropagation();

      if (savedItemsStatus !== "ready") return false;

      const newOptimisticState = !displaySaved;
      setOptimisticSaved(newOptimisticState);

      try {
        if (isSaved && savedItem) {
          await removeSavedItems({ itemId: savedItem.id });
        } else {
          const params = variantId
            ? { productId, variantId }
            : { productId, colourWayId: colourWayId! };
          await addSavedItems(params);

          if (onAdded) {
            try {
              onAdded({ productId });
            } catch (callbackError) {
              console.error("onAdded callback failed", callbackError);
            }
          }
        }

        setOptimisticSaved(null);
        return true;
      } catch (error) {
        console.error("Save for later API call failed:", error);

        setOptimisticSaved(null);
        return false;
      }
    };

    return (
      <div ref={ref} className={className} {...props}>
        <AsyncAction.Root successTimeoutMs={0}>
          <AsyncAction.Button size="icon" onClick={handleOnClick}>
            {displaySaved ? SavedIcon : UnsavedIcon}
          </AsyncAction.Button>
        </AsyncAction.Root>
      </div>
    );
  },
);

const defaultContext: SavedItemContextState = {
  status: "initialising",
  getSavedItems: async () => ({}) as SavedItems,
  addSavedItems: async () => ({}) as AddSavedItemsResponse,
  removeSavedItems: async () => ({}) as SavedItems,
  updateSavedItems: async () => {},
  customerHasSavedItems: false,
  savedItems: {
    itemCount: 0,
    savedItemProducts: [],
    savedItemIds: [],
    isAnonymousCustomer: true,
  },
  SavedIcon: null,
  UnsavedIcon: null,
};

export const SavedItemsContext =
  createContext<SavedItemContextState>(defaultContext);

export const Provider = ({
  SavedIcon,
  UnsavedIcon,
  children,
  chameleonRequestInstance,
}: SavedItemsProviderProps) => {
  const [savedItemsSdk, setSavedItemsSdk] =
    useState<SavedItemsSdkInstance | null>(null);
  const [status, setStatus] = useState<Status>("initialising");
  const [savedItems, setSavedItems] = useState<SavedItems>(
    defaultContext.savedItems,
  );
  const { status: authStatus, authClient } = useAuth();
  const { countryData, keyStoreDataversion, shop } = useStore();

  const deferredSdkRef = useRef<DeferredSavedItemsSdk | null>(null);

  if (!deferredSdkRef.current) {
    let resolver: DeferredSavedItemsSdk["resolve"] | null = null;

    const promise = new Promise<SavedItemsSdkInstance>((resolve) => {
      resolver = resolve;
    });

    deferredSdkRef.current = { promise, resolve: resolver! };
  }

  useEffect(() => {
    if (authStatus === "initialising") return;

    const initializeSavedItems = async () => {
      try {
        const sdk = await initializeSavedItemsSdk({
          chameleonRequestInstance,
          store: countryData.storeCode,
          currency: countryData.defaultCurrency,
          lang: countryData.defaultLanguage,
          sizeSchema: countryData.defaultSizeSchema,
          country: countryData.countryCode,
          keyStoreDataversion,
          getAuthHeaderPredicate: authClient.getAuthHeaderPayload,
          shop,
        });

        setSavedItemsSdk(sdk);
        setSavedItems(sdk.initialSavedItems || defaultContext.savedItems);
        setStatus("ready");
        deferredSdkRef.current?.resolve(sdk);
      } catch (sdkInitError) {
        console.error("Failed to initialize SavedItems SDK:", sdkInitError);
        setStatus("initialising");
      }
    };

    initializeSavedItems();
  }, [
    shop,
    countryData,
    keyStoreDataversion,
    authStatus,
    authClient,
    chameleonRequestInstance,
  ]);

  const getSavedItems = async ({
    expand,
  }: {
    expand?: SavedItemsExpandOption;
  }) => {
    const sdk = await deferredSdkRef.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to fetch saved items. The Saved Items SDK did not initialize",
      );
    }

    const response = await sdk.getSavedItems({ expand });
    setSavedItems(response);

    return response;
  };

  const addSavedItems = async (
    params:
      | {
          productId: number;
          variantId: number;
        }
      | { productId: number; colourWayId: number },
  ) => {
    const sdk = await deferredSdkRef.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to save product to saved items. The Saved Items SDK did not initialize",
      );
    }

    try {
      const response = await sdk.addSavedItems({ items: [params] });

      if (response.savedItemIds.length > 0) {
        setSavedItems((prev) => ({
          ...prev,
          savedItemIds: [...prev.savedItemIds, ...response.savedItemIds],
          itemCount: prev.itemCount + response.savedItemIds.length,
        }));
      }

      return response;
    } catch (error) {
      console.error("Failed to add saved item:", error);
      return { savedItemIds: [] };
    }
  };

  const removeSavedItems = async (params: { itemId: string }) => {
    const sdk = await deferredSdkRef.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to remove product from saved items. The Saved Items SDK did not initialize",
      );
    }

    const idToRemove = savedItems.savedItemIds.find(
      (item) => item.id === params.itemId,
    );
    const productToRemove = savedItems.savedItemProducts.find(
      (item) => item.id === params.itemId,
    );

    if (!idToRemove && !productToRemove) {
      console.warn(`Item with id ${params.itemId} not found in saved items`);
      return savedItems;
    }

    const optimisticState = {
      ...savedItems,
      savedItemIds: savedItems.savedItemIds.filter(
        (item) => item.id !== params.itemId,
      ),
      savedItemProducts: savedItems.savedItemProducts.filter(
        (item) => item.id !== params.itemId,
      ),
      itemCount: Math.max(0, savedItems.itemCount - 1),
    };

    setSavedItems(optimisticState);

    try {
      await sdk.removeSavedItems(params);
      return optimisticState;
    } catch (error) {
      console.error("Failed to remove saved item:", error);

      setSavedItems(savedItems);
      return savedItems;
    }
  };

  const updateSavedItems = async (params: {
    itemId: string;
    variantId: number;
  }) => {
    const sdk = await deferredSdkRef.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to update product in saved items. The Saved Items SDK did not initialize",
      );
    }

    const originalProducts = savedItems.savedItemProducts;

    setSavedItems((prev) => ({
      ...prev,
      savedItemProducts: prev.savedItemProducts.map((product) =>
        product.id === params.itemId
          ? { ...product, variantId: params.variantId }
          : product,
      ),
    }));

    try {
      await sdk.updateSavedItems(params);
    } catch (error) {
      console.error("Failed to update saved item:", error);

      setSavedItems((prev) => ({
        ...prev,
        savedItemProducts: originalProducts,
      }));
    }
  };

  const contextValue: SavedItemContextState =
    status === "ready" && savedItemsSdk
      ? {
          status,
          getSavedItems,
          addSavedItems,
          removeSavedItems,
          updateSavedItems,
          customerHasSavedItems: !!savedItems,
          savedItems: savedItems || [],
          SavedIcon,
          UnsavedIcon,
        }
      : { ...defaultContext, SavedIcon, UnsavedIcon };

  return (
    <SavedItemsContext.Provider value={contextValue}>
      {children}
    </SavedItemsContext.Provider>
  );
};

export const useSavedItems = () => useContext(SavedItemsContext);
